import { Box, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { tokens } from '../../theme/tokens';

/**
 * Prasaar brand mark + wordmark.
 * Inline SVG uses tokens so logo colors follow the central palette.
 */
export function LogoMark({ size = 32, animated = false }) {
  return (
    <Box
      component="svg"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 512 512"
      aria-hidden
      sx={{
        width: size,
        height: size,
        borderRadius: 1.5,
        flexShrink: 0,
        display: 'block',
        boxShadow: `0 4px 14px ${tokens.shellDeep}66`,
        '@keyframes prasaarSpin': {
          to: { transform: 'rotate(360deg)' },
        },
        '& .prasaar-ring': animated
          ? {
              transformBox: 'view-box',
              transformOrigin: 'center',
              animationName: 'prasaarSpin',
              animationTimingFunction: 'linear',
              animationIterationCount: 'infinite',
            }
          : undefined,
        '& .prasaar-ring-a': { animationDuration: '2.8s' },
        '& .prasaar-ring-b': { animationDuration: '1.7s', animationDirection: 'reverse' },
        '& .prasaar-ring-c': { animationDuration: '1.05s' },
        '& .prasaar-ring-d': { animationDuration: '3.6s', animationDirection: 'reverse' },
        '& .prasaar-ring-core': { animationDuration: '1.35s' },
        '@media (prefers-reduced-motion: reduce)': {
          '& .prasaar-ring': { animation: 'none' },
        },
      }}
    >
      <rect width="512" height="512" rx="112" fill={tokens.shell} />
      <g className="prasaar-ring prasaar-ring-a">
        <path
          d="M256 88c92 0 168 76 168 168s-76 168-168 168"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="26"
          strokeLinecap="round"
          opacity="0.22"
        />
      </g>
      <g className="prasaar-ring prasaar-ring-b">
        <path
          d="M256 148c58 0 106 48 106 106s-48 106-106 106"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="26"
          strokeLinecap="round"
          opacity="0.45"
        />
      </g>
      <g className="prasaar-ring prasaar-ring-c">
        <path
          d="M256 200c30 0 54 24 54 54s-24 54-54 54"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="26"
          strokeLinecap="round"
          opacity="0.75"
        />
      </g>
      <g className="prasaar-ring prasaar-ring-d">
        <path
          d="M256 88c-92 0-168 76-168 168s76 168 168 168"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="22"
          strokeLinecap="round"
          opacity="0.12"
        />
      </g>
      <g className="prasaar-ring prasaar-ring-core">
        <circle cx="256" cy="256" r="42" fill={tokens.brand} />
        {animated ? (
          <path
            d="M256 214a42 42 0 0 1 36 21"
            fill="none"
            stroke="#FFFFFF"
            strokeWidth="8"
            strokeLinecap="round"
            opacity="0.9"
          />
        ) : null}
        <circle cx="256" cy="256" r="18" fill="#FFFFFF" opacity="0.95" />
      </g>
    </Box>
  );
}

export function BrandMark({ compact = false, always = false }) {
  const label = compact ? 'Pr' : 'Prasaar';

  return (
    <Box
      component={RouterLink}
      to="/"
      replace
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1.15,
        textDecoration: 'none',
        color: 'inherit',
        transition: 'opacity 220ms ease',
        '&:hover': { opacity: 0.88 },
        ...(!always && compact
          ? { display: { xs: 'inline-flex', md: 'none' } }
          : null),
        ...(!always && !compact
          ? { display: { xs: 'none', md: 'inline-flex' } }
          : null),
      }}
    >
      <LogoMark size={compact ? 28 : 34} />
      <Typography
        component="span"
        color="inherit"
        sx={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 650,
          fontSize: compact ? '1.05rem' : '1.3rem',
          letterSpacing: '-0.03em',
          lineHeight: 1.2,
          color: 'inherit',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}
